import React, { useState } from 'react';
import PaymentMethodPanel from './panels/PaymentMethodPanel';
import ReviewPaymentPanel, { CalculateCostObject } from './panels/ReviewPaymentPanel';
import CancelBookingPanel from './panels/CancelBookingPanel';
import TermsAndConditionsPanel from './panels/TermsAndConditionsPanel';
import { modalTypes } from '../../../../views/booking-overview/BuyerBooking';
import Busy from '../../../Busy';
import { completeBooking, updatePaymentMethodForBooking } from '../../../../views/requests/booking-requests';
import { getErrorMessageForStandardResponse } from '../../../../util/NetworkErrorUtil';
import { useSnackbar } from '../../../../context/snackbar-context';
import type { Booking } from '../../../../types/Booking';
import type { Account } from '../../../../types/Account';
import type { PaymentMethod } from '../../../../types/PaymentMethod';

interface BuyerPendingDialogsProps {
  account: Account;
  bookingBeingActioned: Booking;
  showModal: { show: boolean; type: string };
  setShowModal: (modal: { show: boolean; type: string }) => void;
  handleCloseModals: () => void;
  onDialogSuccess: () => void;
  handleCancelBooking: (booking: Booking) => void;
}

const BuyerPendingDialogs = (props: BuyerPendingDialogsProps) => {
  const {
    account,
    bookingBeingActioned,
    showModal,
    setShowModal,
    handleCloseModals,
    onDialogSuccess,
    handleCancelBooking,
  } = props;

  const [calculateCostObject, setCalculateCostObject] = useState<CalculateCostObject | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>();

  const { snackbarShowMessage } = useSnackbar();

  const showPaymentPanel = showModal.show && showModal.type === modalTypes.payment;
  const showReviewPaymentPanel = showModal.show && showModal.type === modalTypes.review;
  const showCancelPanel = showModal.show && showModal.type === modalTypes.cancel_request;
  const showTermsPanel = showModal.show && showModal.type === modalTypes.termsOnly;

  const handleError = (err: unknown) => {
    snackbarShowMessage(getErrorMessageForStandardResponse(err, 'An error occurred.'), 'error');
  };

  const handleShowReviewPanel = () => {
    setShowModal({ show: true, type: modalTypes.review });
  };

  const handleCompleteBooking = () => {
    if (!selectedPaymentMethod) {
      snackbarShowMessage('Please select a payment method before completing this booking.', 'error');
      return;
    }
    const updateAndCompleteBooking = async () => {
      Busy.set(true);
      try {
        await updatePaymentMethodForBooking({
          bookingId: bookingBeingActioned.id,
          paymentMethodId: selectedPaymentMethod.id,
        });
        await completeBooking({
          bookingId: bookingBeingActioned.id,
          tosDocUrl: bookingBeingActioned.toDocUrl ?? '',
        });
        handleCloseModals();
        snackbarShowMessage(
          'Booking completed successfully! This Booking is now fully approved and your payment is processing.',
          'success',
        );
        onDialogSuccess();
      } catch (err) {
        handleError(err);
      }
      Busy.set(false);
    };
    updateAndCompleteBooking();
  };

  const sendComplete = () => {
    Busy.set(true);
    completeBooking({
      bookingId: bookingBeingActioned.id,
      tosDocUrl: bookingBeingActioned.toDocUrl ?? '',
    })
      .then(() => {
        handleCloseModals();
        snackbarShowMessage('Booking completed successfully! This Booking is now fully approved.', 'success');
        onDialogSuccess();
      })
      .catch(handleError)
      .finally(() => Busy.set(false));
  };

  return (
    <>
      <PaymentMethodPanel
        open={showPaymentPanel}
        booking={bookingBeingActioned}
        account={account}
        handleNext={handleShowReviewPanel}
        setCalculateCostObject={setCalculateCostObject}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        onClose={handleCloseModals}
      />
      <ReviewPaymentPanel
        open={showReviewPaymentPanel}
        calculateCostObject={calculateCostObject}
        completeBooking={handleCompleteBooking}
        bookingToComplete={bookingBeingActioned}
        onClose={handleCloseModals}
      />
      <CancelBookingPanel
        open={showCancelPanel}
        onClose={handleCloseModals}
        cancelBooking={() => handleCancelBooking(bookingBeingActioned)}
        booking={bookingBeingActioned}
      />
      <TermsAndConditionsPanel
        open={showTermsPanel}
        onClose={handleCloseModals}
        completeBooking={sendComplete}
      />
    </>
  );
};

export default BuyerPendingDialogs;

